/**
 * TierCard Component
 *
 * Tier-adaptive card that responds to user's subscription tier:
 * - Layer 1: 12px border radius, white background
 * - Layer 2: 8px border radius, optional premium gold accent
 * - Layer 3: 4px border radius, optional editorial black background
 *
 * Variant Options:
 * - default: Standard white card with border
 * - premium: Gold accent border (Layer 2+), blush background
 * - editorial: Black background with white text (Layer 3 only)
 *
 * Usage:
 * ```tsx
 * import { TierCard } from '@dramadetector/ui';
 *
 * <TierCard variant="default">
 *   <Text>Drama Score: 4.2</Text>
 * </TierCard>
 *
 * <TierCard variant="premium">
 *   <Text className="text-accent-gold">Pattern Detected</Text>
 *   <Text>Your Monday messages are 2x more defensive...</Text>
 * </TierCard>
 * ```
 */
import React from 'react';
import { View } from 'react-native';

export interface TierCardProps {
  variant?: 'default' | 'premium' | 'editorial';
  children: React.ReactNode;
  className?: string;
  testID?: string;
}

export function TierCard({
  variant = 'default',
  children,
  className = '',
  testID,
}: TierCardProps) {
  const baseClasses = 'p-6 shadow-md transition-tier';
  const radiusClass = 'rounded-tier';

  const variantClasses = {
    default: 'bg-white border border-gray-200',
    premium: 'bg-accent-blush border-l-4 border-accent-gold',
    editorial: 'bg-black border border-accent-gold',
  };

  return (
    <View className={`${baseClasses} ${radiusClass} ${variantClasses[variant]} ${className}`} testID={testID}>
      {children}
    </View>
  );
}
